import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuizSources } from "@/hooks/useQuizSources";
import { QuizQuestion } from "@/types/quiz";
import { toast } from "sonner";

interface SelectQuizSourceDialogProps {
  brandId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (questions: QuizQuestion[]) => void;
}

export const SelectQuizSourceDialog = ({
  brandId,
  open,
  onOpenChange,
  onImport,
}: SelectQuizSourceDialogProps) => {
  const { data: sources, isLoading } = useQuizSources(brandId);
  const [selectedSourceId, setSelectedSourceId] = useState<string>('');
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);

  // Filter sources that have completed and have approved questions
  const availableSources = sources?.filter(
    s => s.generation_status === 'completed' && 
         s.generated_questions && 
         s.approved_question_ids.length > 0
  ) || [];

  const selectedSource = availableSources.find(s => s.id === selectedSourceId);
  const approvedQuestions = selectedSource?.generated_questions?.filter(
    q => selectedSource.approved_question_ids.includes(q.id)
  ) || [];

  const toggleQuestion = (questionId: string) => {
    setSelectedQuestionIds(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const selectAll = () => {
    setSelectedQuestionIds(approvedQuestions.map(q => q.id));
  };

  const handleImport = () => {
    const questionsToImport = approvedQuestions.filter(q => 
      selectedQuestionIds.includes(q.id)
    );

    if (questionsToImport.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    onImport(questionsToImport);
    toast.success(`Imported ${questionsToImport.length} questions`);
    onOpenChange(false);
    setSelectedSourceId('');
    setSelectedQuestionIds([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Add Questions from Source</DialogTitle>
          <DialogDescription>
            Select a quiz source and choose which questions to import
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-4 overflow-hidden min-h-0">
          <div className="flex-shrink-0">
            <label className="text-sm font-medium">Quiz Source</label>
            <Select value={selectedSourceId} onValueChange={setSelectedSourceId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a source" />
              </SelectTrigger>
              <SelectContent>
                {isLoading ? (
                  <SelectItem value="loading" disabled>Loading...</SelectItem>
                ) : availableSources.length === 0 ? (
                  <SelectItem value="none" disabled>No sources with approved questions</SelectItem>
                ) : (
                  availableSources.map(source => {
                    const sourceLabel = source.source_type === 'url' 
                      ? source.source_data.url 
                      : source.source_type === 'file' 
                      ? source.source_data.fileName 
                      : source.source_type;
                    
                    return (
                      <SelectItem key={source.id} value={source.id}>
                        {sourceLabel} - {source.approved_question_ids.length} approved questions
                      </SelectItem>
                    );
                  })
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedSource && approvedQuestions.length > 0 && (
            <>
              <div className="flex items-center justify-between flex-shrink-0">
                <p className="text-sm text-muted-foreground">
                  {approvedQuestions.length} approved questions available
                </p>
                <Button variant="outline" size="sm" onClick={selectAll}>
                  Select All
                </Button>
              </div>

              <ScrollArea className="flex-1 border rounded-lg p-4 overflow-y-auto min-h-0">
                <div className="space-y-3">
                  {approvedQuestions.map((question) => (
                    <div key={question.id} className="flex items-start gap-3 p-3 border rounded">
                      <Checkbox
                        checked={selectedQuestionIds.includes(question.id)}
                        onCheckedChange={() => toggleQuestion(question.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{question.question}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Type: {question.type}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!selectedSourceId || selectedQuestionIds.length === 0}
          >
            Import Selected ({selectedQuestionIds.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
